/* eslint-disable @typescript-eslint/naming-convention */
import { BulkActionConfigModel, BulkActionMessage } from '../../models/bulk-action-config.model';
import { MonitoringBulkActionTypeEnum } from '../../enums/monitoring-bulk-action-type.enum';
import { TagModel } from '../../../../Common/Tags/models/tag.model';
import { BulkMentionActionModel } from '../../models/bulk-mention-action.model';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { BulkActionFormExportComponent } from '../BulkActionFormExport/bulk-action-form-export.component';
import { AddTagBulkActionModel, BulkActionFormTagComponent, TaggingMethodSelectionEnum } from '../BulkActionFormTag/bulk-action-form-tag.component';
import { SentimentModel } from '../../models/sentiment.model';
import { BulkActionFormSentimentComponent } from '../BulkActionFormSentiment/bulk-action-form-sentiment.component';
import { SearchCriteriaQuerySentimentsEnum } from '../../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { Folder } from '../../models/folder.model';
import { BulkActionFormFolderComponent, FolderBulkActionParameters } from '../BulkActionFormFolder/bulk-action-form-folder.component';
import { MentionsApiErrorEnum } from '../../../../api/mentions/enum/mentions-api-error.enum';
import { BulkActionFormBlankComponent } from '../BulkActionFormBlank/bulk-action-form-blank.component';
import { BulkActionFormRenormalizeComponent } from '../BulkActionFormRenormalize/bulk-action-form-renormalize.component';
import { BulkActionFormSingleTagComponent } from '../BulkActionFormSingleTag/bulk-action-form-single-tag.component';
import { AddAccountBulkActionModel, BulkActionFormAccountComponent } from '../BulkActionFormAccount/bulk-action-form-account.component';

export const BulkActions: BulkActionConfigModel[] = [
    {
        type: MonitoringBulkActionTypeEnum.export,
        name: 'Monitoring.bulk.action.export.label',
        buttonLabel: 'Monitoring.bulk.action.export.button',

        form: BulkActionFormExportComponent,

        isValid: () => true,
        confirmMessage: () => null,
        prepareAction: () => null,
        actionSuccess: () => null,
        actionError: () => null
    },
    {
        type: MonitoringBulkActionTypeEnum.addFolder,
        name: 'Monitoring.bulk.action.addFolder.label',
        buttonLabel: 'Monitoring.bulk.action.addFolder.button',
        disabledButtonTooltip: 'Monitoring.bulk.action.addFolder.button.disabledTooltip',
        form: BulkActionFormFolderComponent,
        parameters : <FolderBulkActionParameters> {
            showAutoCreateOption: true,
            multiSelection: true
        },

        isValid: (folders: Folder[]) => {
            return folders?.length > 0;
        },
        confirmMessage: (folders: Folder[]) => {
            return {
                key: 'Monitoring.bulk.action.addFolders.confirm',
                args: {
                    name: folders.map(folder => folder.name).join(', ')
                }
            };
        },
        prepareAction: (folders: Folder[]) => {
            const action: BulkMentionActionModel = new BulkMentionActionModel();

            action.currentActionType = MonitoringBulkActionTypeEnum.addFolder;
            action.addFolders = folders.map(folder => folder.id);

            return action;
        },
        actionSuccess: (folders: Folder[], mentionsCount: number) => {
            return {
                severity: messageSeverityEnum.success,
                summary: {key: 'Monitoring.bulk.action.addFolder.success.header'},
                detail: {
                    key: 'Monitoring.bulk.action.addFolders.success.message',
                    args: {
                        name: folders.map(folder => folder.name).join(', '),
                        count: mentionsCount
                    }
                }
            };
        },
        actionError: (folders: Folder[], mentionsCount: number, error: any) => {
            if (error && error.error && error.error.details) {
                for (const errorDetail of error.error.details) {
                    switch (errorDetail.code) {
                        case MentionsApiErrorEnum.folderLimitReached:
                            return {
                                severity: messageSeverityEnum.error,
                                summary: {key: 'MentionFoldersService.saveMentionsFailedHeading'},
                                detail: {key: 'MentionFoldersService.saveMentionsFailedDetail.limitReached'}
                            };
                    }
                }
            }
            return {
                severity: messageSeverityEnum.error,
                summary: {key: 'Monitoring.bulk.action.addFolder.error.header'},
                detail: {
                    key: 'Monitoring.bulk.action.addFolders.error.message',
                    args: {
                        name: folders.map(folder => folder.name).join(', '),
                        count: mentionsCount
                    }
                }
            };
        }
    },
    {
        type: MonitoringBulkActionTypeEnum.removeFolder,
        name: 'Monitoring.bulk.action.removeFolder.label',
        buttonLabel: 'Monitoring.bulk.action.removeFolder.button',
        disabledButtonTooltip: 'Monitoring.bulk.action.addFolder.button.disabledTooltip',
        form: BulkActionFormFolderComponent,
        parameters : <FolderBulkActionParameters> {
            showAutoCreateOption: false,
            multiSelection: true
        },

        isValid: (folders: Folder[]) => {
            return folders?.length > 0;
        },
        confirmMessage: (folders: Folder[]) => {
            return {
                key: 'Monitoring.bulk.action.removeFolders.confirm',
                args: {
                    name: folders.map(folder => folder.name).join(', ')
                }
            };
        },
        prepareAction: (folders: Folder[]) => {
            const action: BulkMentionActionModel = new BulkMentionActionModel();

            action.currentActionType = MonitoringBulkActionTypeEnum.removeFolder;
            action.removeFolders = folders.map(folder => folder.id);

            return action;
        },
        actionSuccess: (folders: Folder[], mentionsCount: number): BulkActionMessage => {
            return {
                severity: messageSeverityEnum.success,
                summary: {key: 'Monitoring.bulk.action.removeFolder.success.header'},
                detail: {
                    key: 'Monitoring.bulk.action.removeFolders.success.message',
                    args: {
                        name: folders.map(folder => folder.name).join(', '),
                        count: mentionsCount
                    }
                }
            };
        },
        actionError: (folders: Folder[], mentionsCount: number, error: any): BulkActionMessage => {
            return {
                severity: messageSeverityEnum.error,
                summary: {key: 'Monitoring.bulk.action.removeFolder.error.header'},
                detail: {
                    key: 'Monitoring.bulk.action.removeFolders.error.message',
                    args: {
                        name: folders.map(folder => folder.name).join(', '),
                        count: mentionsCount
                    }
                }
            };
        }
    },
    {
        type: MonitoringBulkActionTypeEnum.addTags,
        name: 'Monitoring.bulk.action.addTag.label',
        buttonLabel: 'Monitoring.bulk.action.addTag.button.chooseTag',
        disabledButtonTooltip: 'Monitoring.bulk.action.addTag.button.disabledTooltip',
        form: BulkActionFormTagComponent,

        isValid: (model: AddTagBulkActionModel) => {
            if (!model) {
                return false;
            }

            if (model.method === TaggingMethodSelectionEnum.chooseTags) {
                return model.tag && !!model.tag.id;
            } else {
                return true;
            }
        },
        confirmMessage: (model: AddTagBulkActionModel) => {
            if (model.method === TaggingMethodSelectionEnum.chooseTags) {
                return {
                    key: 'Monitoring.bulk.action.addTag.confirm',
                    args: {
                        name: model.tag.name
                    }
                };
            }
        },
        prepareAction: (model: AddTagBulkActionModel) => {
            const action: BulkMentionActionModel = new BulkMentionActionModel();

            if (model.method === TaggingMethodSelectionEnum.chooseTags) {
                action.currentActionType = MonitoringBulkActionTypeEnum.addTags;
                action.addTag = model.tag.id;
                delete action.taggingSavedSearchIds;

            } else if (model.method === TaggingMethodSelectionEnum.basedOnTaggingTopics) {
                action.currentActionType = MonitoringBulkActionTypeEnum.addTagsFromTaggingTopics;
                action.taggingSavedSearchIds = model.taggingSavedSearchIds;
                delete action.addTag;
            }

            return action;
        },
        actionSuccess: (model: AddTagBulkActionModel, mentionsCount: number) => {
            if (model.method === TaggingMethodSelectionEnum.chooseTags) {
                return {
                    severity: messageSeverityEnum.success,
                    summary: {key: 'Monitoring.bulk.action.addTag.success.header'},
                    detail: {
                        key: 'Monitoring.bulk.action.addTag.success.message',
                        args: {
                            name: model.tag.name,
                            count: mentionsCount
                        }
                    }
                };
            } else if (model.method === TaggingMethodSelectionEnum.basedOnTaggingTopics) {
                return {
                    severity: messageSeverityEnum.success,
                    summary: { key: 'Monitoring.bulk.action.addTagBasedOnTaggingTopics.success.header' },
                    detail: { key: 'Monitoring.bulk.action.addTagBasedOnTaggingTopics.success.message' }
                };
            }
        },
        actionError: (model: AddTagBulkActionModel, mentionsCount: number, error: any) => {
            if (model.method === TaggingMethodSelectionEnum.chooseTags) {
                return {
                    severity: messageSeverityEnum.error,
                    summary: {key: 'Monitoring.bulk.action.addTag.error.header'},
                    detail: {
                        key: 'Monitoring.bulk.action.addTag.error.message',
                        args: {
                            name: model.tag.name,
                            count: mentionsCount
                        }
                    }
                };
            } else if (model.method === TaggingMethodSelectionEnum.basedOnTaggingTopics) {
                return {
                    severity: messageSeverityEnum.error,
                    summary: { key: 'Monitoring.bulk.action.addTagBasedOnTaggingTopics.error.header' },
                    detail: { key: 'Monitoring.bulk.action.addTagBasedOnTaggingTopics.error.message' }
                };

                // return {
                //     severity: messageSeverityEnum.error,
                //     summary: { key: 'Monitoring.bulk.action.addTagBasedOnTaggingTopics.partialError.header' },
                //     detail: { key: 'Monitoring.bulk.action.addTagBasedOnTaggingTopics.partialError.message' }
                // };
            }
        }
    },
    {
        type: MonitoringBulkActionTypeEnum.removeTags,
        name: 'Monitoring.bulk.action.removeTag.label',
        buttonLabel: 'Monitoring.bulk.action.removeTag.button',
        disabledButtonTooltip: 'Monitoring.bulk.action.addTag.button.disabledTooltip',
        form: BulkActionFormSingleTagComponent,

        isValid: (tag: TagModel) => {
            return tag && !!tag.id;
        },
        confirmMessage: (tag: TagModel) => {
            return {
                key: 'Monitoring.bulk.action.removeTag.confirm',
                args: {
                    name: tag.name
                }
            };
        },
        prepareAction: (tag: TagModel) => {
            const action: BulkMentionActionModel = new BulkMentionActionModel();

            action.currentActionType = MonitoringBulkActionTypeEnum.removeTags;
            action.removeTag = tag.id;

            return action;
        },
        actionSuccess: (tag: TagModel, mentionsCount: number): BulkActionMessage => {
            return {
                severity: messageSeverityEnum.success,
                summary: {key: 'Monitoring.bulk.action.removeTag.success.header'},
                detail: {
                    key: 'Monitoring.bulk.action.removeTag.success.message',
                    args: {
                        name: tag.name,
                        count: mentionsCount
                    }
                }
            };
        },
        actionError: (tag: TagModel, mentionsCount: number, error: any): BulkActionMessage => {
            return {
                severity: messageSeverityEnum.error,
                summary: {key: 'Monitoring.bulk.action.removeTag.error.header'},
                detail: {
                    key: 'Monitoring.bulk.action.removeTag.error.message',
                    args: {
                        name: tag.name,
                        count: mentionsCount
                    }
                }
            };
        }
    },
    {
        type: MonitoringBulkActionTypeEnum.setSentiment,
        name: 'Monitoring.bulk.action.setSentiment.label',
        buttonLabel: 'Monitoring.bulk.action.setSentiment.button',
        disabledButtonTooltip: 'Monitoring.bulk.action.setSentiment.button.disabledTooltip',
        form: BulkActionFormSentimentComponent,

        isValid: (sentiment: SentimentModel) => {
            return sentiment && !!sentiment.id;
        },
        confirmMessage: (sentiment: SentimentModel) => {
            return {
                key: 'Monitoring.bulk.action.setSentiment.confirm',
                args: {
                    name: sentiment.name
                }
            };
        },
        prepareAction: (sentiment: SentimentModel) => {
            const action: BulkMentionActionModel = new BulkMentionActionModel();

            action.currentActionType = MonitoringBulkActionTypeEnum.setSentiment;

            switch (sentiment.id) {
                case SearchCriteriaQuerySentimentsEnum.positive:
                    action.setSentiment = 1;
                    break;
                case SearchCriteriaQuerySentimentsEnum.neutral:
                    action.setSentiment = 0;
                    break;
                case SearchCriteriaQuerySentimentsEnum.negative:
                    action.setSentiment = -1;
                    break;
                case SearchCriteriaQuerySentimentsEnum.notAvailable:
                    action.currentActionType = MonitoringBulkActionTypeEnum.removeSentiment;
                    action.removeSentiment = true;
                    break;
            }

            return action;
        },
        actionSuccess: (sentiment: SentimentModel, mentionsCount: number): BulkActionMessage => {
            return {
                severity: messageSeverityEnum.success,
                summary: {key: 'Monitoring.bulk.action.setSentiment.success.header'},
                detail: {
                    key: 'Monitoring.bulk.action.setSentiment.success.message',
                    args: {
                        name: sentiment.name,
                        count: mentionsCount
                    }
                }
            };
        },
        actionError: (sentiment: SentimentModel, mentionsCount: number, error: any): BulkActionMessage => {
            return {
                severity: messageSeverityEnum.error,
                summary: {key: 'Monitoring.bulk.action.setSentiment.error.header'},
                detail: {
                    key: 'Monitoring.bulk.action.setSentiment.error.message',
                    args: {
                        name: sentiment.name,
                        count: mentionsCount
                    }
                }
            };
        }
    },
    {
        type: MonitoringBulkActionTypeEnum.renormalize,
        name: 'Monitoring.bulk.action.renormalize.label',
        buttonLabel: 'Monitoring.bulk.action.renormalize.button',
        form: BulkActionFormRenormalizeComponent,
        isValid: (value: any) => true,
        confirmMessage: (value: any) => null,
        prepareAction: (preserveEdit: boolean) => {
            const action: BulkMentionActionModel = new BulkMentionActionModel();
            action.currentActionType = MonitoringBulkActionTypeEnum.renormalize;
            action.renormalize = true;
            action.preserveEdit = preserveEdit;
            return action;
        },
        actionSuccess: (value: any, count: number) => {
            return {
                severity: messageSeverityEnum.success,
                summary: { key: 'Monitoring.bulk.action.renormalize.growlSuccessHeading' },
                detail: {
                    key: 'Monitoring.bulk.action.renormalize.growlSuccessMessage',
                    args: {
                        count: count
                    }
                }
            };
        },
        actionError: (value: any, count: number, error: any) => {
            return {
                severity: messageSeverityEnum.error,
                summary: { key: 'Monitoring.bulk.action.renormalize.growlErrorHeading' },
                detail: {
                    key: 'Monitoring.bulk.action.renormalize.growlErrorMessage',
                    args: {
                        count: count
                    }
                }
            };
        }
    },
    {
        type: MonitoringBulkActionTypeEnum.copyMentionsToAccount,
        name: 'Monitoring.bulk.action.copyToAccount.label',
        buttonLabel: 'Monitoring.bulk.action.copyToAccount.button',
        disabledButtonTooltip: 'Monitoring.bulk.action.copyToAccount.button.disabledTooltip',
        form: BulkActionFormAccountComponent,
        parameters : <FolderBulkActionParameters> {
            showAutoCreateOption: true
        },

        isValid: (model: AddAccountBulkActionModel) => {
            return model && model.account && !!model.account.id && model.folder && !!model.folder.id;
        },
        confirmMessage: (model: AddAccountBulkActionModel) => {
            return {
                key: 'Monitoring.bulk.action.copyToAccount.confirm',
                args: {
                    name: model.account.name
                }
            };
        },
        prepareAction: (model: AddAccountBulkActionModel) => {
            const action: BulkMentionActionModel = new BulkMentionActionModel();
            action.targetAccountId = model.account.id;
            action.targetFolderId = model.folder.id;
            return action;
        },
        actionSuccess: (model: AddAccountBulkActionModel, mentionsCount: number) => {
            return {
                severity: messageSeverityEnum.success,
                summary: {key: 'Monitoring.bulk.action.copyToAccount.success.header'},
                detail: {
                    key: 'Monitoring.bulk.action.copyToAccount.success.message',
                    args: {
                        name: model.account.name,
                        count: mentionsCount
                    }
                }
            };
        },
        actionError: (model: AddAccountBulkActionModel, mentionsCount: number, error: any) => {
            if (error && error.error && error.error.details) {
                for (const errorDetail of error.error.details) {
                    switch (errorDetail.code) {
                        case MentionsApiErrorEnum.folderLimitReached:
                            return {
                                severity: messageSeverityEnum.error,
                                summary: {key: 'MentionFoldersService.saveMentionsFailedHeading'},
                                detail: {key: 'MentionFoldersService.saveMentionsFailedDetail.limitReached'}
                            };
                    }
                    if (errorDetail?.message?.includes('licenses')) {
                        return {
                            severity: messageSeverityEnum.error,
                            summary: {key: 'Monitoring.bulk.action.copyToAccount.error.noLicenses.header'},
                            detail: {key: 'Monitoring.bulk.action.copyToAccount.error.noLicenses.message'}
                        };
                    }
                }
            }
            return {
                severity: messageSeverityEnum.error,
                summary: {key: 'Monitoring.bulk.action.copyToAccount.error.header'},
                detail: {
                    key: 'Monitoring.bulk.action.copyToAccount.error.message',
                    args: {
                        name: model.account.name,
                        count: mentionsCount
                    }
                }
            };
        }
    },
    {
        type: MonitoringBulkActionTypeEnum.deleteFromAccount,
        name: 'Monitoring.result.deleteMentionsFromAccountButtonLabel',
        buttonLabel: 'Monitoring.result.deleteMentionsFromAccountButtonLabel',
        form: BulkActionFormBlankComponent,
        isValid: () => true,
        confirmMessage: () => null,
        prepareAction: () => null,
        actionSuccess: () => null,
        actionError: () => null
    }
];
